import { TextField, Button, Typography, Container, Box, FormControlLabel, Checkbox, Collapse, Alert, IconButton, Grid } from '@mui/material';
import '../styles/Login.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { RootState, useAppDispatch, useAppSelector } from '../store/store';
import { performLogIn } from '../store/auth-slice/auth-slice';
import { AppRoutes } from '../axios/endPoints';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import { Roles } from './layout/menu-items';

const schema = (t: Function) => yup.object().shape({
    email: yup.string().email(t('LOG_IN_EMAIL_VALIDATION')).required(t('LOG_IN_EMAIL_REQUIRED')),
    password: yup.string().min(8, t('LOG_IN_PASSWORD_VALIDATION')).required('LOG_IN_PASSWORD_REQUIRED'),
    remember: yup.boolean()
}).required();

const Login = () => {
    const { loading } = useAppSelector((state: RootState) => state.auth);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { control, handleSubmit, formState: { errors } } = useForm({ 
        reValidateMode: 'onBlur',
        resolver: yupResolver(schema(t)), 
        defaultValues: {
            email: '',
            password: '',
            remember: false
        } 
    });
    
    const [message, setMessage] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    
    function submit(data: any) {
        dispatch(performLogIn({
            email: data.email,
            password: data.password,
            remember: data.remember
        })).then((data : any) => {
            if (data.error !== null && data.error !== undefined) {
                setMessage('BE_USERMGMT_CREDENTIALS_INVALID');
            }
            else if (
                data.error === undefined &&
                data.payload &&
                data.payload.user !== null &&
                location.pathname === AppRoutes.LOG_IN
            ) {
                if (data.payload.success === false) {
                    setMessage(data.payload.message);
                }
                else {
                    if (data.payload.userRoles?.[0] === Roles.MLViewer) {
                        navigate(AppRoutes.ML_PREDICTION);
                    }
                    else {
                        navigate(AppRoutes.ML_CREATOR);
                    }
                }
            }
        })
    }

    useEffect(() => {
        document.title = t('LOG_IN_PAGE_TITLE');
    }, [])
  
    return (
        <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold" mb={1}>
                    {t('LOG_IN_SIGN_IN_TITLE')}
                </Typography>

                <Box sx={{ mt: 1, width: '100%' }}>
                    <form onSubmit={handleSubmit(submit)} noValidate>
                        <Controller
                            control={control}
                            name="email"
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    variant="outlined"
                                    label={t('LOG_IN_EMAIL_LABEL')}
                                    type="email"
                                    error={!!error}
                                    helperText={error ? errors.email?.message : ''}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="password"
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    margin="normal"
                                    fullWidth
                                    variant="outlined"
                                    label={t('LOG_IN_PASSWORD_LABEL')}
                                    type="password"
                                    error={!!error}
                                    helperText={error ? errors.password?.message : ''}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="remember"
                            render={({ field: { onChange, value, ...field } }) => (
                                <FormControlLabel
                                    label={t('LOG_IN_REMEMBER_ME')}
                                    control={<Checkbox onChange={onChange} checked={value} {...field} />}
                                />
                            )}
                        />

                        <Collapse in={message !== null}>
                            <Alert
                                severity="error"
                                action={
                                    <IconButton
                                        aria-label="close"
                                        color="inherit"
                                        size="small"
                                        onClick={() => setMessage(null)}
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                }
                            >
                                {t(`${message}`)}
                            </Alert>
                        </Collapse>

                        <Button
                            className="button-color"
                            disabled={loading}
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                        >
                            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : t('LOG_IN_TEXT')}
                        </Button>

                        <Grid container justifyContent="flex-end">
                            <Grid item>
                                <Link className="button-text-color" href={AppRoutes.FORGOT_PASSWORD} variant="body2">
                                    {t('LOG_IN_FORGOT_PASSWORD_BUTTON')}
                                </Link>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </Box>
        </Container>
    );
}

export default Login;